import type {
  LBNodeData,
  TextTemplateChunk,
  VariableRef,
} from '@/proto/logiblocks/logiblocks';
import { ALERT_TYPE_LABELS } from '@/common/alertType';
import { VariableSource, VariableType } from '@/proto/common';
import type { GraphData } from './types';
import {
  getAncestorVariables,
  getVariableType,
  isSystemRef,
  lookupVariableRef,
  variableRefName,
} from './variables/variableUtils';
import { getNodeTypeOutputs } from './variables/variableDefinitions';
import { hasTemplateContent } from './variables/TemplatedTextArea/templateSerialization';
import { getSystemVariable } from './variables/systemVariableCatalog';
import { OutputType } from './types';
import { getLBNodeInfo, getLBNodeType } from './nodes/registry';
import { bytesEqual, lbNodeDataToBytes } from './serialization';

/**
 * Categories of validation errors for programmatic handling.
 */
export type ValidationErrorType =
  | 'REQUIRED_FIELD' // Field is empty/missing (string, array, variable ref)
  | 'VARIABLE_ERROR' // Variable ref issues (not ancestor, unknown variable, unknown type)
  | 'SYNTAX_ERROR' // Template syntax (malformed {{...}}, unclosed braces)
  | 'VALUE_ERROR' // Value doesn't match expected type (invalid alert type)
  | 'UNIMPLEMENTED_NODE'; // Node type not yet implemented

/**
 * A validation error with a type for programmatic handling and a message for display.
 */
export interface ValidationError {
  /** The category/type of the validation error */
  type: ValidationErrorType;
  /** The field that has the error (e.g., "Recipient teams", "Message template") */
  field: string;
  /** Human-readable error message for display to users */
  message: string;
  /** Optional, ephemeral grouping key for the publish control's unresolved-
   * field count. This is presentation metadata, not persistent error identity. */
  displayGroupKey?: string;
}

export interface UnresolvedFieldSummary {
  unresolvedFieldCount: number;
  affectedNodeIds: string[];
  firstAffectedNodeId: string | undefined;
}

/** Groups raw validation messages into user-visible fields without changing
 * the raw error map that remains authoritative for publish blocking. */
export function getUnresolvedFieldSummary(
  validationErrors: ReadonlyMap<string, readonly ValidationError[]>
): UnresolvedFieldSummary {
  let unresolvedFieldCount = 0;
  const affectedNodeIds: string[] = [];

  validationErrors.forEach((errors, nodeId) => {
    if (errors.length === 0) return;
    affectedNodeIds.push(nodeId);
    const groups = new Set(
      errors.map((error) =>
        error.displayGroupKey === undefined
          ? `field:${JSON.stringify(error.field)}`
          : `explicit:${JSON.stringify(error.displayGroupKey)}`
      )
    );
    unresolvedFieldCount += groups.size;
  });

  return {
    unresolvedFieldCount,
    affectedNodeIds,
    firstAffectedNodeId: affectedNodeIds[0],
  };
}

/** Set of valid alert type values */
const VALID_ALERT_TYPES = new Set(Object.keys(ALERT_TYPE_LABELS));

/** Validation messages */
export const MSG_REQUIRED = 'This field must have a value';
export const MSG_INVALID_VALUE = 'Invalid value';
export const MSG_INVALID_VARIABLE = 'Contains an invalid variable reference';

/**
 * Creates a validation error for malformed template variable syntax.
 */
function getMalformedVariableError(fieldName: string): ValidationError {
  return {
    type: 'SYNTAX_ERROR',
    field: fieldName,
    message: MSG_INVALID_VARIABLE,
  };
}

/**
 * Validates that a variable reference points to a valid ancestor variable.
 *
 * @param ref - The variable reference to validate
 * @param nodeId - The ID of the node containing this reference
 * @param graph - The entire ReactFlow graph
 * @param fieldName - Human-readable field name for error messages
 * @returns A ValidationError if validation fails, undefined if valid
 */
export function validateVariableRef(
  ref: VariableRef | undefined,
  nodeId: string,
  graph: GraphData,
  fieldName: string
): ValidationError | undefined {
  if (!ref || !ref.node_id || !ref.variable_name) {
    return {
      type: 'REQUIRED_FIELD',
      field: fieldName,
      message: MSG_REQUIRED,
    };
  }

  // Publish validation rejects a reference with no source.
  if (ref.source === VariableSource.SOURCE_UNSPECIFIED) {
    return {
      type: 'VARIABLE_ERROR',
      field: fieldName,
      message: MSG_INVALID_VALUE,
    };
  }

  // System variables are always available — no ancestor lookup needed. None has
  // fields, so system.now.foo names nothing.
  if (isSystemRef(ref)) {
    if (getSystemVariable(variableRefName(ref))) {
      return undefined;
    }
    return {
      type: 'VARIABLE_ERROR',
      field: fieldName,
      message: MSG_INVALID_VALUE,
    };
  }

  // Check if the referenced node is an ancestor
  const ancestorVars = getAncestorVariables(nodeId, graph.nodes, graph.edges);
  const ancestorNode = ancestorVars.find((av) => av.nodeId === ref.node_id);
  if (!ancestorNode) {
    return {
      type: 'VARIABLE_ERROR',
      field: fieldName,
      message: MSG_INVALID_VALUE,
    };
  }

  // The ancestor must publish the variable, or a meta-variable the derived name
  // is a real field of.
  if (lookupVariableRef(ref, ancestorNode.filteredOutputVariables)) {
    return undefined;
  }

  return {
    type: 'VARIABLE_ERROR',
    field: fieldName,
    message: MSG_INVALID_VALUE,
  };
}

/**
 * Validates that a value is valid for a given variable type.
 *
 * @param ref - The variable reference (used to look up the variable's type)
 * @param value - The value to validate
 * @param graph - The entire ReactFlow graph
 * @param fieldName - Human-readable field name for error messages
 * @returns A ValidationError if validation fails, undefined if valid
 */
export function validateVariableValue(
  ref: VariableRef | undefined,
  value: string,
  graph: GraphData,
  fieldName: string
): ValidationError | undefined {
  if (!ref) return undefined;

  const varType = getVariableType(ref, graph.nodes);
  if (varType === undefined) {
    return {
      type: 'VARIABLE_ERROR',
      field: fieldName,
      message: MSG_INVALID_VALUE,
    };
  }

  switch (varType) {
    case VariableType.ALERT_TYPE:
      if (!VALID_ALERT_TYPES.has(value)) {
        return {
          type: 'VALUE_ERROR',
          field: fieldName,
          message: MSG_INVALID_VALUE,
        };
      }
      break;
    case VariableType.STRING:
    case VariableType.TEXT:
      // No type-specific validation for strings
      break;
  }

  return undefined;
}

/**
 * Validates the variable references in a template's chunk field. A chunk
 * with nothing set, or a reference with no source, is an error, as it is when
 * the backend publishes.
 */
export function validateTemplateChunks(
  chunks: TextTemplateChunk[],
  nodeId: string,
  graph: GraphData,
  fieldName: string
): ValidationError[] {
  const errors: ValidationError[] = [];
  for (const chunk of chunks) {
    if (
      chunk.text === undefined &&
      chunk.variable === undefined &&
      chunk.user_mention === undefined
    ) {
      errors.push({
        type: 'SYNTAX_ERROR',
        field: fieldName,
        message: MSG_INVALID_VALUE,
      });
    } else if (chunk.text !== undefined) {
      // `{{node.name}}` in a text chunk is not a variable reference. It would
      // go out in the message as written.
      if (chunk.text.includes('{{')) {
        errors.push(getMalformedVariableError(fieldName));
      }
    } else if (chunk.variable === undefined) {
      continue;
    } else {
      const refError = validateVariableRef(
        chunk.variable,
        nodeId,
        graph,
        fieldName
      );
      if (refError) {
        errors.push({
          type: refError.type,
          field: fieldName,
          message: MSG_INVALID_VARIABLE,
        });
      }
    }
  }
  return errors;
}

/**
 * Validates that a required string field is not empty.
 *
 * @param value - The string value to validate
 * @param fieldName - Human-readable field name for error messages
 * @returns A ValidationError if validation fails (empty/whitespace), undefined if valid
 */
export function validateRequiredString(
  value: string | undefined,
  fieldName: string
): ValidationError | undefined {
  if (!value || value.trim() === '') {
    return {
      type: 'REQUIRED_FIELD',
      field: fieldName,
      message: MSG_REQUIRED,
    };
  }
  return undefined;
}

/**
 * Validates that a required template field's chunks have something in them. A
 * variable or a mention counts; text counts unless it's only whitespace.
 */
export function validateRequiredTemplate(
  templateChunks: TextTemplateChunk[],
  fieldName: string
): ValidationError | undefined {
  if (hasTemplateContent(templateChunks)) {
    return undefined;
  }
  return { type: 'REQUIRED_FIELD', field: fieldName, message: MSG_REQUIRED };
}

/**
 * Validates that a required array field is not empty.
 *
 * @param value - The array to validate
 * @param fieldName - Human-readable field name for error messages
 * @returns A ValidationError if validation fails (undefined/empty), undefined if valid
 */
export function validateRequiredArray<T>(
  value: T[] | undefined,
  fieldName: string
): ValidationError | undefined {
  if (!value || value.length === 0) {
    return {
      type: 'REQUIRED_FIELD',
      field: fieldName,
      message: MSG_REQUIRED,
    };
  }
  return undefined;
}

/**
 * Collects non-undefined errors into an array.
 *
 * @param errors - Variable number of ValidationErrors or undefined values
 * @returns Array of ValidationErrors if any errors exist, undefined if all inputs were undefined
 */
export function collectErrors(
  ...errors: (ValidationError | undefined)[]
): ValidationError[] | undefined {
  const filtered = errors.filter((e): e is ValidationError => e !== undefined);
  return filtered.length > 0 ? filtered : undefined;
}

/** Field label for user variable validation errors. */
export const USER_VARIABLES_FIELD = 'User variables';

/** ASCII identifier: letter or underscore, then letters/digits/underscores.
 * Must match `_USER_VARIABLE_NAME_RE` in `backend/logiblocks/graph/variable_utils.py`.
 */
const USER_VARIABLE_NAME_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;

/**
 * Validates the per-instance user variable declarations on a node.
 */
export function validateUserVariables(nodeData: LBNodeData): ValidationError[] {
  const errors: ValidationError[] = [];
  const nodeType = getLBNodeType(nodeData);
  // Which outputs a node publishes depends on its settings, so which names are taken does too.
  // An SMS node that isn't waiting for a reply doesn't publish `reply`, so an author may use it.
  const staticNames = new Set(
    nodeType
      ? getNodeTypeOutputs(nodeType, nodeData).map(
          (output) => output.variable!.name
        )
      : []
  );
  const seen = new Set<string>();

  for (const [index, uv] of (nodeData.user_variables ?? []).entries()) {
    const displayGroupKey = `user-variable:${index}`;
    if (!USER_VARIABLE_NAME_RE.test(uv.name)) {
      errors.push({
        type: 'VALUE_ERROR',
        field: USER_VARIABLES_FIELD,
        displayGroupKey,
        message: `Variable name '${uv.name}' must be ASCII letters, digits, and underscores, starting with a letter or underscore.`,
      });
      continue;
    }
    if (uv.display_name.trim() === '') {
      errors.push({
        type: 'REQUIRED_FIELD',
        field: USER_VARIABLES_FIELD,
        displayGroupKey,
        message: `Variable '${uv.name}' must have a display name.`,
      });
      continue;
    }
    if (seen.has(uv.name)) {
      errors.push({
        type: 'VALUE_ERROR',
        field: USER_VARIABLES_FIELD,
        displayGroupKey,
        message: `Variable name '${uv.name}' is declared more than once on this node.`,
      });
      continue;
    }
    if (staticNames.has(uv.name)) {
      errors.push({
        type: 'VALUE_ERROR',
        field: USER_VARIABLES_FIELD,
        displayGroupKey,
        message: `Variable name '${uv.name}' collides with a built-in output of this node type.`,
      });
      continue;
    }
    seen.add(uv.name);
  }
  return errors;
}

/**
 * Whether a node's data is still exactly its type's freshly-created default -
 * i.e. the author hasn't touched it yet. Such a node is often invalid (empty
 * required fields), but that's a false alarm rather than a real problem: the
 * author hasn't had a chance to configure it. Callers that surface validation
 * errors cosmetically (a red ring, a disabled publish toggle) should exclude
 * these nodes; callers that gate actual persistence/publish should not - this
 * only affects presentation, never the underlying errors used to block those.
 */
export function isUnconfiguredDefaultNode(nodeData: LBNodeData): boolean {
  const nodeInfo = getLBNodeInfo(nodeData);
  if (!nodeInfo) return false;
  return bytesEqual(
    lbNodeDataToBytes(nodeData),
    lbNodeDataToBytes(nodeInfo.createDefaultNodeData())
  );
}

/**
 * Computes validation errors for all nodes in the graph.
 *
 * @param graph - The entire ReactFlow graph (nodes and edges)
 * @returns Map of node IDs to their ValidationErrors
 */
export function computeValidationErrors(
  graph: GraphData
): Map<string, ValidationError[]> {
  const errors = new Map<string, ValidationError[]>();

  for (const node of graph.nodes) {
    const nodeData = node.data as LBNodeData;
    const nodeInfo = getLBNodeInfo(nodeData);
    const nodeErrors: ValidationError[] = [];

    if (nodeInfo?.getValidationErrors) {
      const perNodeErrors = nodeInfo.getValidationErrors(
        node.id,
        nodeData,
        graph
      );
      if (perNodeErrors) {
        nodeErrors.push(...perNodeErrors);
      }
    }

    // Framework-level user variable validation runs for every node, mirroring
    // the backend's base-class default in `LBNode.get_validation_errors`.
    nodeErrors.push(...validateUserVariables(nodeData));

    // Structural check: SUBGRAPH_PARENT nodes must have at least one child.
    // Checked here (not in getValidationErrors) so it never blocks the
    // customize panel's Save button — only the canvas publish path.
    if (nodeInfo?.outputType === OutputType.SUBGRAPH_PARENT) {
      const hasChildren = graph.nodes.some((n) => n.parentId === node.id);
      if (!hasChildren) {
        nodeErrors.push({
          type: 'REQUIRED_FIELD',
          field: 'body',
          message: 'Filter node must contain at least one action.',
        });
      }
    }

    if (nodeErrors.length > 0) {
      errors.set(node.id, nodeErrors);
    }
  }

  return errors;
}
